import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SignatureCanvas from 'react-signature-canvas';
import { bolAPI } from '../utils/api';
import { formatDecimal, formatDecimalOrNull, toNumber } from '../utils/format';
import type { BOL } from '../types';
import { parseHazmatNotes, stripHazmatNotes, basicDescription } from '../utils/hazmat';
import PDFViewer from '../components/PDFViewer';

interface PreviewDocument {
  url: string;
  fileName: string;
  description: string;
  fileType: 'pdf' | 'image' | 'other';
  blobUrl?: string;
}

/**
 * One stroke as the signature pad records it. react-signature-canvas bundles
 * signature_pad 2.x, but its published types describe signature_pad 4 (which
 * this project also installs), so strokes are cast to this where they cross.
 */
type SignatureStroke = Array<{ x: number; y: number; time: number; color?: string }>;

const BOLDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bol, setBol] = useState<BOL | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showOSDModal, setShowOSDModal] = useState(false);
  const [osdComment, setOsdComment] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [podName, setPodName] = useState('');
  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewLoading, setPreviewLoading] = useState(false);
  const signaturePadRef = useRef<SignatureCanvas>(null);
  const sigContainerRef = useRef<HTMLDivElement>(null);
  // A copy of the signature's strokes, kept so the canvas can be redrawn if
  // it is wiped under the driver - on their phones the keyboard opening has
  // done exactly that.
  const signatureStrokesRef = useRef<SignatureStroke[]>([]);
  const signingRef = useRef(false);
  const refitAfterStrokeRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Touch gesture refs for pinch zoom
  const lastTouchDistance = useRef<number | null>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchBOL = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        console.log('Fetching BOL leg:', id);
        const response = await bolAPI.getBOL(parseInt(id));
        
        console.log('BOL response:', response);
        if (response.success && response.data) {
          setBol(response.data);
        } else {
          toast.error('Failed to load BOL details');
          navigate('/bol');
        }
      } catch (error) {
        console.error('Error fetching BOL:', error);
        toast.error('Failed to load BOL details');
        navigate('/bol');
      } finally {
        setLoading(false);
      }
    };

    fetchBOL();
  }, [id, navigate]);

  // Fit the signature canvas's drawing surface to its on-screen box, at the
  // screen's pixel density - signature_pad records strokes in on-screen pixels,
  // so a surface of any other size draws them away from the finger - and
  // redraw the saved strokes. The redraw runs even when the size is unchanged:
  // on some phones the keyboard opening wipes the canvas without resizing it.
  const fitSignatureCanvas = useCallback(() => {
    const pad = signaturePadRef.current;
    const container = sigContainerRef.current;
    if (!pad || !container) return;

    // Mid-stroke, a redraw would cut the stroke; do it when the finger lifts.
    if (signingRef.current) {
      refitAfterStrokeRef.current = true;
      return;
    }

    const canvas = pad.getCanvas();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.getContext('2d')?.scale(ratio, ratio);
    }

    const strokes = signatureStrokesRef.current;
    if (strokes.length > 0) {
      pad.fromData(strokes.map((stroke) => stroke.slice()) as unknown as Parameters<SignatureCanvas['fromData']>[0]);
    } else {
      pad.clear();
    }
  }, []);

  const handleSignatureBegin = useCallback(() => {
    signingRef.current = true;
    // After the keyboard is hidden with the down button, the name field keeps
    // focus, and the keyboard comes back when the finger lifts.
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      active.blur();
    }
  }, []);

  const handleSignatureEnd = useCallback(() => {
    signingRef.current = false;
    const pad = signaturePadRef.current;
    if (pad) {
      signatureStrokesRef.current = (pad.toData() as unknown as SignatureStroke[]).map((stroke) => stroke.slice());
    }
    if (refitAfterStrokeRef.current) {
      refitAfterStrokeRef.current = false;
      fitSignatureCanvas();
    }
  }, [fitSignatureCanvas]);

  const clearSignature = useCallback(() => {
    signatureStrokesRef.current = [];
    signaturePadRef.current?.clear();
  }, []);

  // Real props of the component; its published types lost them along with the
  // rest of signature_pad 2.x's options.
  const signaturePadEvents = { onBegin: handleSignatureBegin, onEnd: handleSignatureEnd } as object;

  useEffect(() => {
    if (!showDeliveryModal) return;
    signatureStrokesRef.current = [];
    fitSignatureCanvas();

    // Redraw whenever the page or the visible area (the keyboard) changes size,
    // or the browser reports it dropped the canvas contents.
    const viewport = window.visualViewport;
    const canvas = signaturePadRef.current?.getCanvas();
    window.addEventListener('resize', fitSignatureCanvas);
    viewport?.addEventListener('resize', fitSignatureCanvas);
    canvas?.addEventListener('contextrestored', fitSignatureCanvas);
    return () => {
      window.removeEventListener('resize', fitSignatureCanvas);
      viewport?.removeEventListener('resize', fitSignatureCanvas);
      canvas?.removeEventListener('contextrestored', fitSignatureCanvas);
    };
  }, [showDeliveryModal, fitSignatureCanvas]);

  useEffect(() => {
    if (showOSDModal && !selectedPhoto) {
      // Start camera when modal opens and no photo captured yet
      startCamera();
    }
    
    return () => {
      // Stop camera when modal closes
      stopCamera();
    };
  }, [showOSDModal, selectedPhoto]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `osd_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
          setSelectedPhoto(file);
          setPhotoPreview(canvas.toDataURL('image/jpeg'));
          stopCamera();
        }
      }, 'image/jpeg', 0.95);
    }
  };

  const handleStartTransit = async () => {
    if (!id || !bol) return;
    
    try {
      setUpdating(true);
      console.log('Starting transit for BOL leg:', id);
      
      // Status ID 4 = "In Transit" based on legstatus table
      const response = await bolAPI.updateBOL(parseInt(id), { status: 4 } as any);
      
      if (response.success && response.data) {
        // Update local state with new status
        setBol(prev => prev ? { ...prev, status: response.data!.status, statusId: response.data!.statusId } : null);
        toast.success('Status updated to In Transit');
        // Navigate back to dashboard
        navigate('/');
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelivered = async () => {
    if (!id || !bol) return;
    
    // Validate inputs
    if (!podName.trim()) {
      toast.error('Please enter POD Name');
      return;
    }
    
    // toData, not isEmpty: a signature redrawn after a resize reports isEmpty().
    if (!signaturePadRef.current || signaturePadRef.current.toData().length === 0) {
      toast.error('Please provide a signature');
      return;
    }
    
    try {
      setUpdating(true);
      
      // Get signature as base64 with higher quality
      const signatureData = signaturePadRef.current.toDataURL('image/png', 1.0);
      
      // Get current date and time in local timezone
      const now = new Date();
      
      // Format date as YYYY-MM-DD (ensuring local timezone)
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const podDate = `${year}-${month}-${day}`;
      
      // Format time as HHMM (no colons, no seconds)
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const podTime = `${hours}${minutes}`;
      
      console.log('POD Date:', podDate, 'POD Time:', podTime);
      
      // Status ID 5 = "Delivered" based on legstatus table
      const response = await bolAPI.updateBOL(parseInt(id), {
        status: 5,
        podName: podName,
        podDate: podDate,
        podTime: podTime,
        signature: signatureData
      } as any);
      
      if (response.success && response.data) {
        toast.success('Delivery confirmed!');
        setShowDeliveryModal(false);
        // Navigate back to dashboard
        navigate('/');
      } else {
        toast.error(response.message || 'Failed to mark as delivered');
      }
    } catch (error) {
      console.error('Error marking delivered:', error);
      toast.error('Failed to mark as delivered');
    } finally {
      setUpdating(false);
    }
  };

  const handleReportOSD = async () => {
    if (!id || !bol) return;
    
    // Validate inputs
    if (!osdComment.trim()) {
      toast.error('Please enter a comment describing the issue');
      return;
    }
    
    if (!selectedPhoto) {
      toast.error('Please capture or select a photo');
      return;
    }
    
    try {
      setUpdating(true);
      
      // Create form data for file upload
      const formData = new FormData();
      formData.append('photo', selectedPhoto);
      formData.append('comment', osdComment);
      formData.append('status', '7'); // Exception status
      
      const response = await bolAPI.reportOSD(parseInt(id), formData);
      
      if (response.success) {
        toast.success('OSD report submitted');
        setShowOSDModal(false);
        setOsdComment('');
        setSelectedPhoto(null);
        setPhotoPreview('');
        // Navigate back to dashboard
        navigate('/');
      } else {
        toast.error(response.message || 'Failed to submit OSD report');
      }
    } catch (error) {
      console.error('Error reporting OSD:', error);
      toast.error('Failed to submit OSD report');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Waiting for Driver Asgn': 'bg-orange-100 text-orange-800',
      'Waiting for Pickup': 'bg-blue-100 text-blue-800',
      'In Transit': 'bg-indigo-100 text-indigo-800',
      'Delivered': 'bg-green-100 text-green-800',
      'Complete': 'bg-green-100 text-green-800',
      'Exception': 'bg-red-100 text-red-800',
      'VOID': 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString: string | null | undefined) => {
    if (!timeString) return '';
    // Handle time format HH:MM:SS
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getFileType = (fileName: string): 'pdf' | 'image' | 'other' => {
    const ext = fileName.toLowerCase().split('.').pop() || '';
    if (ext === 'pdf') return 'pdf';
    // Not svg: it is an image format that can run script, so it is saved, not shown.
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image';
    return 'other';
  };

  /**
   * Image types that render without running code. An object URL runs on this
   * app's origin, where the driver's sign-in token lives, so anything else —
   * an uploaded .svg or .html — is given an inert type and can only be saved.
   */
  const SAFE_IMAGE_TYPES: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp'
  };

  const openDocumentPreview = async (doc: any) => {
    const fileType = getFileType(doc.fileName);
    const description = doc.description || 'Document';

    setPreviewZoom(1);
    // Shown at once in a loading state, filled in when the fetch returns.
    setPreviewDocument({ url: '', fileName: doc.fileName, description, fileType, blobUrl: undefined });
    setPreviewLoading(true);

    try {
      // Through the API, which checks the leg is this driver's. Every type is
      // fetched — not only PDFs and images — so nothing downstream (download,
      // print, share, open in tab) ever points at storage. This used to build
      // a public blob URL from VITE_DOCUMENT_BASE_URL.
      const data = await bolAPI.getDocument(id!, doc.fileName);
      const ext = doc.fileName.toLowerCase().split('.').pop() || '';
      const type = fileType === 'pdf'
        ? 'application/pdf'
        : SAFE_IMAGE_TYPES[ext] ?? 'application/octet-stream';
      const blobUrl = URL.createObjectURL(new Blob([data], { type }));

      // url and blobUrl are the same local object URL, so every place that
      // used the storage URL now uses this instead.
      setPreviewDocument({ url: blobUrl, fileName: doc.fileName, description, fileType, blobUrl });
    } catch (error) {
      console.error('Error loading document:', error);
      // Keep the modal open but show an error state
      toast.error('Failed to load document preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeDocumentPreview = () => {
    // Clean up blob URL if it exists
    if (previewDocument?.blobUrl) {
      URL.revokeObjectURL(previewDocument.blobUrl);
    }
    setPreviewDocument(null);
    setPreviewZoom(1);
  };

  const handleDownload = () => {
    if (!previewDocument) return;
    const link = document.createElement('a');
    link.href = previewDocument.blobUrl || previewDocument.url;
    link.download = previewDocument.fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleZoomIn = () => {
    setPreviewZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setPreviewZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setPreviewZoom(1);
  };

  // Touch gesture handlers for pinch zoom
  const getTouchDistance = (touches: React.TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      lastTouchDistance.current = getTouchDistance(e.touches);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance.current !== null) {
      const currentDistance = getTouchDistance(e.touches);
      const delta = currentDistance - lastTouchDistance.current;
      
      // Adjust zoom based on pinch delta
      if (Math.abs(delta) > 5) {
        const zoomDelta = delta * 0.005; // Sensitivity adjustment
        setPreviewZoom(prev => {
          const newZoom = prev + zoomDelta;
          return Math.max(0.5, Math.min(3, newZoom));
        });
        lastTouchDistance.current = currentDistance;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    lastTouchDistance.current = null;
  }, []);

  const handlePrint = async () => {
    if (!previewDocument) return;
    
    // Check if we can use the Web Share API for mobile (Android)
    if (navigator.share && previewDocument.fileType === 'pdf') {
      try {
        // Fetch the file and create a blob for sharing
        const response = await fetch(previewDocument.url);
        const blob = await response.blob();
        const file = new File([blob], previewDocument.fileName, { type: 'application/pdf' });
        
        await navigator.share({
          files: [file],
          title: previewDocument.description,
        });
        return;
      } catch (error) {
        // If share fails or is cancelled, fall through to other methods
        console.log('Share cancelled or failed, trying print...');
      }
    }

    // For images, create a printable page
    if (previewDocument.fileType === 'image') {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${previewDocument.description}</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                img { max-width: 100%; height: auto; page-break-inside: avoid; }
              }
              body { 
                margin: 0; 
                padding: 20px; 
                display: flex; 
                justify-content: center; 
                align-items: center;
                min-height: 100vh;
                box-sizing: border-box;
              }
              img { max-width: 100%; height: auto; }
            </style>
          </head>
          <body>
            <img src="${previewDocument.url}" alt="${previewDocument.description}" onload="setTimeout(() => { window.print(); }, 100);" />
          </body>
          </html>
        `);
        printWindow.document.close();
      }
      return;
    }

    // For PDFs, create an iframe and trigger print
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.src = previewDocument.url;
    
    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          // If iframe print fails (cross-origin), open in new tab for user to print manually
          window.open(previewDocument.url, '_blank');
        }
        // Remove iframe after a delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 500);
    };
    
    document.body.appendChild(iframe);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!bol) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-gray-500">
        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-lg font-medium">BOL not found</p>
      </div>
    );
  }

  // The hazmat description lives inside the notes text. Parsed once here so
  // the card renders it structured and the Notes card can show what is left.
  /**
   * Open the bill of lading itself in the existing document preview.
   *
   * The driver server generates it — a port of the FMSWeb3 generator — rather
   * than proxying across services, so no cross-service auth is involved. The
   * fetch goes through the API client so the Bearer token rides along; the
   * endpoint is scoped to this driver's own legs.
   */
  const handleViewBolPdf = async () => {
    setPreviewZoom(1);
    setPreviewDocument({
      url: '',
      fileName: `BOL_${bol.bolNumber || bol.idbols || id}.pdf`,
      description: 'Bill of Lading',
      fileType: 'pdf',
      blobUrl: undefined,
    });
    setPreviewLoading(true);
    try {
      const blob = await bolAPI.getBOLPdf(id!);
      setPreviewDocument({
        url: '',
        fileName: `BOL_${bol.bolNumber || bol.idbols || id}.pdf`,
        description: 'Bill of Lading',
        fileType: 'pdf',
        blobUrl: URL.createObjectURL(blob),
      });
    } catch (error) {
      console.error('Error loading BOL PDF:', error);
      toast.error('Could not load the bill of lading');
      setPreviewDocument(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const hazmat = parseHazmatNotes(bol.Notes);
  const plainNotes = stripHazmatNotes(bol.Notes);

  const freightTotals = (bol.freight ?? []).reduce(
    (acc, item: any) => ({
      pieces: acc.pieces + toNumber(item.pieces),
      weight: acc.weight + toNumber(item.weight),
    }),
    { pieces: 0, weight: 0 }
  );

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="mb-4 flex items-center">
        <button
          onClick={() => navigate('/bol')}
          className="mr-3 p-2 hover:bg-gray-100 rounded-full"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          {/* The carrier the printed BOL is headed with. Where a tenant
              originates bills of lading for another entity this is the
              override, so the screen agrees with the paper in the cab. */}
          <h1 className="text-xl font-bold text-gray-900 truncate">
            {bol.headerName || 'BOL Details'}
          </h1>
          <p className="text-sm text-gray-500">
            BOL #{bol.bolNumber || bol.idbols || 'N/A'}
          </p>
          {bol.customerName && (
            <p className="text-sm font-medium text-indigo-600 truncate">{bol.customerName}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void handleViewBolPdf()}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-700 active:bg-gray-200"
            aria-label="View bill of lading"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-xs font-semibold">BOL</span>
          </button>
          {getStatusBadge(bol.status)}
        </div>
      </div>

      {/* Hazardous materials.

          First card on the screen, before pickup: a driver needs to know what
          they are carrying and who to call before anything else, and at an
          incident this is the screen they will have in their hand. Read-only —
          the description is the shipper's declaration, not the driver's. */}
      {hazmat && (
        <div className="bg-white rounded-lg shadow-lg mb-3 border-2 border-red-600 overflow-hidden">
          <div className="bg-red-600 px-4 py-2 flex items-center gap-2">
            <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="font-bold text-white uppercase tracking-wide text-sm">Hazardous Materials</h3>
          </div>

          <div className="p-4">
            {hazmat.entries.length > 0 ? (
              <div className="space-y-2 mb-3">
                {hazmat.entries.map((entry, i) => (
                  <div key={i} className="border-l-4 border-red-500 pl-3">
                    <div className="text-sm font-bold text-gray-900">{basicDescription(entry)}</div>
                    {(entry.packages || entry.quantity) && (
                      <div className="text-xs text-gray-600 mt-0.5">
                        {[entry.packages, entry.quantity].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-600 mb-3">
                This load is placarded but no material description was supplied.
              </div>
            )}

            {/* Tappable, because at an incident this is a phone call. */}
            {hazmat.emergencyPhone && (
              <a
                href={`tel:${hazmat.emergencyPhone.replace(/[^0-9+]/g, '')}`}
                className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-2 active:bg-red-100"
              >
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-red-700">
                    Emergency — 24 hour
                  </div>
                  <div className="text-lg font-bold text-red-700 truncate">{hazmat.emergencyPhone}</div>
                  {hazmat.emergencyContract && (
                    <div className="text-xs text-gray-600 truncate">{hazmat.emergencyContract}</div>
                  )}
                </div>
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </a>
            )}

            {(hazmat.ergGuide || hazmat.placards) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
                {hazmat.ergGuide && (
                  <div><span className="text-gray-500">ERG guide:</span> <span className="font-semibold">{hazmat.ergGuide}</span></div>
                )}
                {hazmat.placards && (
                  <div><span className="text-gray-500">Placards:</span> <span className="font-semibold">{hazmat.placards}</span></div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pickup Section */}
      <div className="bg-white rounded-lg shadow mb-3 p-4">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Pickup</h2>
        </div>
        
        <div className="space-y-2">
          <div>
            <div className="text-xs text-gray-500 uppercase">Location</div>
            <div className="font-medium text-gray-900">{bol.PickName}</div>
            {bol.PickAddress && <div className="text-sm text-gray-600">{bol.PickAddress}</div>}
            <div className="text-sm text-gray-600">
              {bol.PickCity}, {bol.PickState} {bol.PickZip}
            </div>
          </div>
          
          <div className="flex space-x-4 pt-2">
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase">Date</div>
              <div className="text-sm font-medium text-gray-900">{formatDate(bol.pickupDate)}</div>
            </div>
            {bol.pickupTime && (
              <div className="flex-1">
                <div className="text-xs text-gray-500 uppercase">Time</div>
                <div className="text-sm font-medium text-gray-900">{formatTime(bol.pickupTime)}</div>
              </div>
            )}
          </div>

          {bol.specialInstructions && (
            <div className="pt-2 border-t border-gray-100">
              <div className="text-xs text-gray-500 uppercase mb-1">Special Instructions</div>
              <div className="text-sm text-gray-700">{bol.specialInstructions}</div>
            </div>
          )}
        </div>
      </div>

      {/* Delivery Section */}
      <div className="bg-white rounded-lg shadow mb-3 p-4">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Delivery</h2>
        </div>
        
        <div className="space-y-2">
          <div>
            <div className="text-xs text-gray-500 uppercase">Location</div>
            <div className="font-medium text-gray-900">{bol.DropName}</div>
            {bol.DropAddress && <div className="text-sm text-gray-600">{bol.DropAddress}</div>}
            <div className="text-sm text-gray-600">
              {bol.DropCity}, {bol.DropState} {bol.DropZip}
            </div>
          </div>
          
          <div className="flex space-x-4 pt-2">
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase">Date</div>
              <div className="text-sm font-medium text-gray-900">{formatDate(bol.deliveryDate)}</div>
            </div>
            {bol.deliveryTime && (
              <div className="flex-1">
                <div className="text-xs text-gray-500 uppercase">Time</div>
                <div className="text-sm font-medium text-gray-900">{formatTime(bol.deliveryTime)}</div>
              </div>
            )}
          </div>

          {bol.deliveryInstructions && (
            <div className="pt-2 border-t border-gray-100">
              <div className="text-xs text-gray-500 uppercase mb-1">Delivery Instructions</div>
              <div className="text-sm text-gray-700">{bol.deliveryInstructions}</div>
            </div>
          )}
        </div>
      </div>

      {/* POD Section - if delivered */}
      {bol.PODName && (
        <div className="bg-white rounded-lg shadow mb-3 p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Proof of Delivery</h3>
          <div className="space-y-1">
            <div>
              <span className="text-xs text-gray-500">Received by: </span>
              <span className="text-sm font-medium">{bol.PODName}</span>
            </div>
            {bol.PODDate && (
              <div>
                <span className="text-xs text-gray-500">Date: </span>
                <span className="text-sm font-medium">{formatDate(bol.PODDate)}</span>
                {bol.PODTime && <span className="text-sm font-medium"> at {formatTime(bol.PODTime)}</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes. The hazmat block is lifted out and shown in its own card
          above, so it is not repeated here as raw delimited text. */}
      {plainNotes && (
        <div className="bg-white rounded-lg shadow mb-3 p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
          <div className="text-sm text-gray-700 whitespace-pre-wrap">{plainNotes}</div>
        </div>
      )}
      {/* Freight Information */}
      {bol.freight && bol.freight.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-3 p-4">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <h3 className="font-semibold text-gray-900">Freight Details</h3>
          </div>

          {/* Totals up front. A driver checking a load against the paperwork
              wants the shipment weight, not to add the lines up themselves.
              Summed from the freight rows already on screen rather than from a
              separate server field, so the total can never disagree with the
              lines beneath it. */}
          <div className="flex items-center justify-between bg-gray-100 rounded-lg px-3 py-2 mb-3">
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">Total pieces</div>
              <div className="text-base font-bold text-gray-900">{freightTotals.pieces}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">Total weight</div>
              <div className="text-base font-bold text-gray-900">
                {formatDecimal(freightTotals.weight)} lbs
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {bol.freight.map((item: any) => (
              <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-3 pb-2 border-b border-gray-200 grid grid-cols-2 gap-3">
                  {item.description && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Description</div>
                      <div className="text-sm font-semibold text-gray-900">{item.description}</div>
                    </div>
                  )}
                  {item.customerRef && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Customer Ref</div>
                      <div className="text-sm font-medium text-gray-900">{item.customerRef}</div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2 mb-3">
                  <div className="grid grid-cols-2 gap-3">
                    {item.mawb && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase">MAWB</div>
                        <div className="text-sm font-medium text-gray-900">{item.mawb}</div>
                      </div>
                    )}
                    {item.hawb && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase">HAWB</div>
                        <div className="text-sm font-medium text-gray-900">{item.hawb}</div>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Pieces</div>
                      <div className="text-sm font-medium text-gray-900">{item.pieces || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Weight</div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatDecimalOrNull(item.weight) ? `${formatDecimalOrNull(item.weight)} lbs` : 'N/A'}
                      </div>
                    </div>
                  </div>

                  {(toNumber(item.length) || toNumber(item.width) || toNumber(item.height)) > 0 && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Dimensions (L×W×H)</div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatDecimal(item.length)} × {formatDecimal(item.width)} × {formatDecimal(item.height)} in
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Documents */}
      {bol.documents && bol.documents.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-3 p-4 pb-40">
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <h3 className="font-semibold text-gray-900">Documents</h3>
          </div>
          <div className="space-y-2">
            {bol.documents.map((doc: any) => (
              <button
                key={doc.id}
                onClick={() => openDocumentPreview(doc)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex items-center flex-1 min-w-0">
                  {getFileType(doc.fileName) === 'pdf' ? (
                    <svg className="w-8 h-8 text-red-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  ) : getFileType(doc.fileName) === 'image' ? (
                    <svg className="w-8 h-8 text-blue-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-gray-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {doc.description || 'Document'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{doc.fileName}</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 space-y-2">
        {bol.status === 'Waiting for Pickup' ? (
          <button
            onClick={handleStartTransit}
            disabled={updating}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Updating...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Start Transit
              </>
            )}
          </button>
        ) : bol.status === 'In Transit' ? (
          <>
            <button
              onClick={() => setShowDeliveryModal(true)}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Mark as Delivered
            </button>
            <button
              onClick={() => setShowOSDModal(true)}
              className="w-full bg-yellow-600 text-white py-3 rounded-lg font-medium hover:bg-yellow-700 transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Report OSD
            </button>
          </>
        ) : bol.status === 'Delivered' || bol.status === 'Complete' ? (
          <div className="text-center py-3 text-green-600 font-medium">
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Delivery Complete
          </div>
        ) : (
          <div className="text-center py-3 text-gray-500">
            <p className="text-sm">No actions available for this status</p>
            <p className="text-xs mt-1">Status: {bol.status}</p>
          </div>
        )}
      </div>

      {/* Delivery Modal */}
      {showDeliveryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Confirm Delivery</h3>
              <button
                onClick={() => {
                  setShowDeliveryModal(false);
                  setPodName('');
                  signaturePadRef.current?.clear();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* POD Name Input */}
              <div>
                <label htmlFor="podName" className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="podName"
                  value={podName}
                  onChange={(e) => setPodName(e.target.value)}
                  enterKeyHint="done"
                  onKeyDown={(e) => {
                    // Done closes the keyboard, which otherwise covers the signature box.
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                  placeholder="Enter recipient's name"
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Signature Pad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Signature <span className="text-red-500">*</span>
                </label>
                <div 
                  ref={sigContainerRef}
                  className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white" 
                  style={{ width: '100%', height: '200px' }}
                >
                  {/* Resizing and redrawing are fitSignatureCanvas's job: the library would wipe the signature whenever the keyboard opened or closed. */}
                  <SignatureCanvas
                    ref={signaturePadRef}
                    clearOnResize={false}
                    {...signaturePadEvents}
                    canvasProps={{
                      className: 'signature-canvas'
                    }}
                  />
                </div>
                <button
                  onClick={clearSignature}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear Signature
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setShowDeliveryModal(false);
                    setPodName('');
                    signaturePadRef.current?.clear();
                  }}
                  disabled={updating}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelivered}
                  disabled={updating}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {updating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Confirming...
                    </>
                  ) : (
                    'Confirm Delivery'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OSD Modal */}
      {showOSDModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Report OSD</h3>
              <button
                onClick={() => {
                  setShowOSDModal(false);
                  setOsdComment('');
                  setSelectedPhoto(null);
                  setPhotoPreview('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Photo Capture */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photo <span className="text-red-500">*</span>
                </label>
                {photoPreview ? (
                  <div className="relative">
                    <img src={photoPreview} alt="Preview" className="w-full h-64 object-cover rounded-lg" />
                    <button
                      onClick={() => {
                        setSelectedPhoto(null);
                        setPhotoPreview('');
                        startCamera();
                      }}
                      className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-64 object-cover rounded-lg bg-black"
                    />
                    <button
                      onClick={capturePhoto}
                      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white text-gray-800 px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors shadow-lg flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Capture Photo
                    </button>
                  </div>
                )}
              </div>

              {/* Comment Input */}
              <div>
                <label htmlFor="osdComment" className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="osdComment"
                  value={osdComment}
                  onChange={(e) => setOsdComment(e.target.value)}
                  placeholder="Describe the overage, shortage, or damage..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setShowOSDModal(false);
                    setOsdComment('');
                    setSelectedPhoto(null);
                    setPhotoPreview('');
                  }}
                  disabled={updating}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReportOSD}
                  disabled={updating}
                  className="flex-1 px-4 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {updating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 bg-black flex flex-col z-50" style={{ margin: 0, padding: 0 }}>
          {/* Header with controls */}
          <div className="flex items-center justify-between px-3 py-2 bg-gray-900 border-b border-gray-700 shrink-0">
            <div className="flex-1 min-w-0 mr-2">
              <h3 className="text-white font-medium truncate text-sm">{previewDocument.description}</h3>
              <p className="text-gray-400 text-xs truncate">{previewDocument.fileName}</p>
            </div>
            <div className="flex items-center gap-1">
              {/* Zoom controls - always show for PDFs and images */}
              <button
                onClick={handleZoomOut}
                className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Zoom Out"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
              </button>
              <button
                onClick={handleResetZoom}
                className="px-2 py-1 text-white text-xs font-medium hover:bg-gray-700 rounded-lg transition-colors min-w-[50px]"
                title="Reset Zoom"
              >
                {Math.round(previewZoom * 100)}%
              </button>
              <button
                onClick={handleZoomIn}
                className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Zoom In"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </button>
              <div className="w-px h-6 bg-gray-600 mx-1"></div>
              {/* Print button */}
              <button
                onClick={handlePrint}
                className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Print"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
              {/* Download button */}
              <button
                onClick={handleDownload}
                className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Download"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              {/* Open in new tab */}
              <a
                href={previewDocument.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Open in new tab"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <div className="w-px h-6 bg-gray-600 mx-1"></div>
              {/* Close button */}
              <button
                onClick={closeDocumentPreview}
                className="p-2 text-white hover:bg-red-600 rounded-lg transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Preview content - full screen with touch support */}
          <div 
            ref={previewContainerRef}
            className="flex-1 flex flex-col min-h-0" 
            style={{ backgroundColor: '#404040', touchAction: 'pan-x pan-y' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {previewLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p>Loading document...</p>
                </div>
              </div>
            ) : previewDocument.fileType === 'pdf' ? (
              previewDocument.blobUrl ? (
                // PDF viewer using client-side PDF.js rendering (works on all platforms including mobile)
                <PDFViewer
                  url={previewDocument.blobUrl}
                  zoom={previewZoom}
                  onZoomChange={setPreviewZoom}
                />
              ) : (
                // Fallback for when blob isn't available
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white p-8">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p className="text-lg font-medium mb-2">PDF Preview</p>
                    <p className="text-sm text-gray-400 mb-4">Loading PDF...</p>
                    <button
                      onClick={handleDownload}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download PDF
                    </button>
                  </div>
                </div>
              )
            ) : previewDocument.fileType === 'image' ? (
              <div 
                className="w-full h-full overflow-auto flex items-center justify-center p-4"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <img
                  src={previewDocument.blobUrl || previewDocument.url}
                  alt={previewDocument.description}
                  className="max-w-none shadow-2xl"
                  style={{ 
                    transform: `scale(${previewZoom})`, 
                    transformOrigin: 'center center',
                    transition: 'transform 0.1s ease-out'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `
                      <div class="text-center text-white p-8">
                        <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p class="text-lg font-medium">Unable to load image</p>
                        <p class="text-sm text-gray-400 mt-2">Click download to view the file</p>
                      </div>
                    `;
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-white p-8">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p className="text-lg font-medium">Preview not available</p>
                  <p className="text-sm text-gray-400 mt-2">Click download to view this file</p>
                  <button
                    onClick={handleDownload}
                    className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download File
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BOLDetailPage; 